import { calculateUsageCost, TieredPriceProductId } from "@project/common";
import type { IntlShape } from "react-intl";
import { defineMessages } from "react-intl";
import { formatUSD } from "./costDisplay.js";
import { sum } from "es-toolkit";

export enum BillingCategory {
  MapsAndData = "mapsAndData",
  AIAndProcessing = "AIAndProcessing",
  TeamAndAddOns = "teamAndAddOns",
}

const billingCategoryLabels = defineMessages<BillingCategory>({
  [BillingCategory.MapsAndData]: {
    id: "categories.mapsAndData",
    defaultMessage: "Maps & data",
  },
  [BillingCategory.AIAndProcessing]: {
    id: "categories.AIAndProcessing",
    defaultMessage: "AI & processing",
  },
  [BillingCategory.TeamAndAddOns]: {
    id: "categories.teamAndAddOns",
    defaultMessage: "Team & add-ons",
  },
});

const PRODUCT_ID_TO_CATEGORY: Readonly<Record<TieredPriceProductId, BillingCategory>> =
  Object.freeze({
    [TieredPriceProductId.TieredMapSession]: BillingCategory.MapsAndData,
    [TieredPriceProductId.TieredCustomStyle]: BillingCategory.MapsAndData,
    [TieredPriceProductId.TieredTileUsage]: BillingCategory.MapsAndData,
    [TieredPriceProductId.TieredCustomTileset]: BillingCategory.MapsAndData,
    [TieredPriceProductId.TieredInputToken]: BillingCategory.AIAndProcessing,
    [TieredPriceProductId.TieredOutputToken]: BillingCategory.AIAndProcessing,
    [TieredPriceProductId.TieredProcessing]: BillingCategory.AIAndProcessing,
    [TieredPriceProductId.TieredStorage]: BillingCategory.AIAndProcessing,
    [TieredPriceProductId.TieredAccountUser]: BillingCategory.TeamAndAddOns,
  });

interface ReceiptLine {
  category: BillingCategory;
  label: string;
  subtotal: string;
}

export type ProductUsageSums = Record<TieredPriceProductId, number>;

export interface BillSummary {
  grandTotal: string;
  receipt: ReceiptLine[];
}

/**
 * Computes the cost for each product and groups the results by billing
 * category, returning both a grand total and an itemised receipt.
 */
export function buildBillSummary(
  productIds: TieredPriceProductId[],
  usageSums: ProductUsageSums,
  intl: IntlShape,
): BillSummary {
  const billMatrix: [TieredPriceProductId, number][] = productIds.map(id => [
    id,
    calculateUsageCost(usageSums[id], id),
  ]);

  const subtotalByCategory = billMatrix.reduce<Partial<Record<BillingCategory, number>>>(
    (acc, [productId, cost]) => {
      const category = PRODUCT_ID_TO_CATEGORY[productId];
      acc[category] = (acc[category] ?? 0) + cost;
      return acc;
    },
    {},
  );

  const receipt: ReceiptLine[] = (
    Object.entries(subtotalByCategory) as [BillingCategory, number][]
  ).map(([category, subtotal]) => ({
    category,
    label: intl.formatMessage(billingCategoryLabels[category]),
    subtotal: formatUSD(intl, subtotal),
  }));

  const grandTotal = formatUSD(
    intl,
    sum(Object.values(subtotalByCategory)),
  );

  return { grandTotal, receipt };
}
